/**
 * useInvestorPortalPreview — the "view as investor" payload.
 *
 * ir_previewInvestorPortal returns the portal exactly as one investor sees it,
 * computed server-side against that investor (published updates only, their
 * documents, their holdings). The portal hooks call `fetchPortalPreview` from
 * inside their own queryFn while a preview is active, so all four surfaces —
 * dashboard, documents, timeline, consents — share ONE cached round trip
 * instead of fetching the payload each.
 */
import { useQuery } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { getFunctionsForCallable } from '../../config/firebase';
import { useOrganization } from '../../contexts/OrganizationContext';
import { useInvestorPreview } from '../../contexts/InvestorPreviewContext';
import type {
  Investor,
  Holding,
  FundingEvent,
  ShareClass,
} from '../../types/investor-relations';
import type { InvestorDocument } from './useInvestorDocuments';
import type { InvestorUpdate } from './useInvestorUpdates';
import type { ConsentRequest } from './useConsents';

// ============================================================================
// Types (mirror functions/src/investor-relations/services/portalPreviewService)
// ============================================================================

export interface HoldingWithFundingEvents extends Holding {
  readonly fundingEvents: readonly FundingEvent[];
}

export type PortalPreviewWarningCode =
  | 'no_portal_login'
  | 'investor_not_active'
  | 'holdings_not_linked'
  | 'no_holdings'
  | 'no_documents'
  | 'no_updates';

export interface PortalPreviewWarning {
  readonly code: PortalPreviewWarningCode;
  readonly message: string;
  readonly count?: number;
}

export interface InvestorPortalPreview {
  readonly investor: Investor;
  readonly holdings: readonly HoldingWithFundingEvents[];
  readonly shareClasses: readonly ShareClass[];
  readonly documents: readonly InvestorDocument[];
  readonly updates: readonly InvestorUpdate[];
  readonly consents: readonly ConsentRequest[];
  readonly warnings: readonly PortalPreviewWarning[];
}

// ============================================================================
// Query keys
// ============================================================================

export const investorPreviewKeys = {
  all: ['investorPortalPreview'] as const,
  portal: (orgId: string, investorId: string) =>
    [...investorPreviewKeys.all, orgId, investorId] as const,
};

// ============================================================================
// Shared fetcher
// ============================================================================

/**
 * One definition of the preview query, shared by the hook below and by every
 * portal hook's `fetchQuery` call. They must use the SAME key and fetcher —
 * a hook whose queryFn re-entered fetchQuery on its own key would await its
 * own in-flight promise and hang.
 */
export function portalPreviewQueryOptions(orgId: string, investorId: string) {
  return {
    queryKey: investorPreviewKeys.portal(orgId, investorId),
    queryFn: async (): Promise<InvestorPortalPreview> => {
      const fn = getFunctionsForCallable<unknown, InvestorPortalPreview>(
        'ir_previewInvestorPortal'
      );
      const result = await fn({ organizationId: orgId, investorId });
      return result.data;
    },
    staleTime: 1000 * 60,
  };
}

/**
 * Fetch (or reuse) the preview payload. Called from inside other hooks'
 * queryFns — `fetchQuery` dedupes concurrent callers and caches the result, so
 * four portal surfaces cost one callable invocation.
 */
export function fetchPortalPreview(
  queryClient: QueryClient,
  orgId: string,
  investorId: string
): Promise<InvestorPortalPreview> {
  return queryClient.fetchQuery(portalPreviewQueryOptions(orgId, investorId));
}

// ============================================================================
// Hook — for the preview banner itself
// ============================================================================

export function useInvestorPortalPreviewData() {
  const { previewInvestorId } = useInvestorPreview();
  const { currentOrganization } = useOrganization();
  const orgId = currentOrganization?.organizationId ?? '';

  return useQuery({
    ...portalPreviewQueryOptions(orgId, previewInvestorId ?? ''),
    enabled: !!orgId && !!previewInvestorId,
  });
}
